import React, { useState, useEffect } from "react";
import {
  Box, Button, TextField, Typography, Paper, List, ListItem, ListItemText, IconButton, ListItemSecondaryAction,
  Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem, CircularProgress, Collapse, Card, CardContent, CardActions
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import Dashboard from "./Dashboard";
import ManageUsers from "./ManageUsers";
import ManageAccounts from "./ManageAccounts";

function ApplicationsList({ user }) {
  const [applications, setApplications] = useState([]);
  const [open, setOpen] = useState(false);
  const [formMode, setFormMode] = useState('quick'); // 'quick' or 'advanced'
  const [form, setForm] = useState({
    address: "",
    proposedDevelopment: "",
    attachments: [],
    assessFiles: false, // New state for checkbox
    status: "new"
  });
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assessmentText, setAssessmentText] = useState("");

  // For popup menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const navigate = useNavigate();

  // New state for creating application
  const [creating, setCreating] = useState(false);
  const [showManageUsers, setShowManageUsers] = useState(false);
  const [showManageAccounts, setShowManageAccounts] = useState(false);
  
  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);
  
  // Category filtering state
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(null);

  const fetchApplications = (category = null) => {
    let url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/applications/`;
    
    if (category && category !== 'all') {
      if (category === 'uncategorized') {
        // For uncategorized, we need to get all applications and filter client-side
        // since the backend doesn't support filtering by null values directly in URL params
        url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/applications/`;
      } else {
        url = `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/applications/?category=${category}`;
      }
    }
    
    fetch(url, {
      credentials: "include"
    })
      .then(res => res.json())
      .then(apps => {
        // If filtering for uncategorized, filter client-side for null/undefined categories
        if (category === 'uncategorized') {
          const filteredApps = apps.filter(app => !app.category);
          setApplications(filteredApps);
        } else {
          setApplications(apps);
        }
      })
      .catch(err => console.error('Error fetching applications:', err));
  };

  useEffect(() => {
    fetchApplications(categoryFilter === 'all' ? null : categoryFilter);
  }, [categoryFilter]);

  const handleLogout = async () => {
    await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/logout/", {
      method: "POST",
      credentials: "include",
    });
    window.location.reload();
  };

  const handleChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (name === "attachments") {
      setForm((prev) => ({
        ...prev,
        attachments: files ? Array.from(files) : [],
      }));
    } else if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setForm((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...files],
      }));
    }
  };

  const removeFile = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const data = new FormData();
      
      let endpoint;
      
      if (formMode === 'quick') {
        // For quick mode, call the create_and_analyze endpoint
        endpoint = "/create_and_analyze_applications/";
        // Only send attachments for quick mode - let the AI extract address and development details
        if (form.attachments && form.attachments.length > 0) {
          for (let i = 0; i < form.attachments.length; i++) {
            data.append("attachments", form.attachments[i]);
          }
        }
      } else {
        // For advanced mode, use the regular applications endpoint
        endpoint = "/applications/";
        data.append("address", form.address);
        data.append("proposedDevelopment", form.proposedDevelopment);
        data.append("status", form.status || "new");
        data.append("assess_files", form.assessFiles);
        
        if (form.attachments && form.attachments.length > 0) {
          for (let i = 0; i < form.attachments.length; i++) {
            data.append("attachments", form.attachments[i]);
          }
        }
      }

      const res = await axios.post((process.env.REACT_APP_API_URL || "http://localhost:8000") + endpoint, data, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // For quick mode, the response will be different (analysis results)
      // For advanced mode, it's the application object
      if (formMode === 'quick') {
        // The response contains analysis results, we need to refresh the applications list
        // since the application was created on the backend
        const applicationsResponse = await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + "/applications/", {
          credentials: "include"
        });
        const updatedApplications = await applicationsResponse.json();
        setApplications(updatedApplications);
      } else {
        // Advanced mode returns the application object directly
        const newApp = res.data;
        setApplications((prev) => [...prev, newApp]);
      }
      
      setForm({ address: "", proposedDevelopment: "", attachments: [], assessFiles: true, status: "new" });
      setFormMode('quick'); // Reset to quick mode
      setOpen(false);
      // Reset file input if needed
      const fileInput = document.getElementById("attachments-input");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      console.error("Error creating application:", err);
      // Handle error (e.g., show notification)
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    await fetch((process.env.REACT_APP_API_URL || "http://localhost:8000") + `/applications/${id}`, {
      method: "DELETE",
      credentials: "include"
    });
    setApplications((prev) => prev.filter(app => app.id !== id));
  };

  // Popup menu handlers
  const handleListItemClick = (event, app) => {
    event.preventDefault();
    setSelectedApp(app);
    setMenuAnchorEl(null); // Not used with anchorPosition
    setMenuPosition({
      top: event.clientY,
      left: event.clientX,
    });
  };
  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedApp(null);
    setAnalyzeLoading(false);
  };

  const handleCategoryChange = (category) => {
    setCategoryFilter(category);
  };

  // Analyze with AI
  const handleAnalyze = () => {
    if (!selectedApp) return;
    // Redirect to /analyzeform and pass selectedApp as state
    navigate("/analyzeform", { state: { application: selectedApp } });
    handleMenuClose();
  };

  // Account menu handlers
  const handleAccountMenu = (event) => setAnchorEl(event.currentTarget);
  const handleAccountMenuClose = () => setAnchorEl(null);

  if (selectedApp) {
    return (
      <Dashboard
        application={selectedApp}
        onBack={() => setSelectedApp(null)}
        onAnalyze={() => { 
          navigate("/analyzeform", { state: { application: selectedApp } });
        }}
        onShowAssessment={() => {
          setAssessmentText(selectedApp.initial_assessment || "No assessment available.");
          setAssessmentOpen(true);
        }}
      />
    );
  }

  if (showManageUsers) {
    return (
      <ManageUsers onClose={() => setShowManageUsers(false)} />
    );
  }

  if (showManageAccounts) {
    return (
      <ManageAccounts onClose={() => setShowManageAccounts(false)} />
    );
  }

  if (showManageAccounts) {
    return (
      <ManageAccounts onClose={() => setShowManageAccounts(false)} />
    );
  }

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", background: "#f7fafc" }}>
      {/* Top right account button */}
      <Box sx={{ position: "absolute", top: 16, right: 32 }}>
        <IconButton
          size="large"
          edge="end"
          color="inherit"
          onClick={handleAccountMenu}
        >
          <AccountCircle fontSize="large" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleAccountMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem disabled>
            <Typography variant="subtitle2">
              {user?.name} ({Array.isArray(user?.roles) ? user.roles.join(", ") : user?.role})
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleAccountMenuClose}>My Account</MenuItem>
          {Array.isArray(user?.roles) && user.roles.includes("Superadmin") && (
            <>
              <MenuItem
                onClick={() => {
                  setShowManageUsers(true);
                  handleAccountMenuClose();
                }}
              >
                Manage users
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setShowManageAccounts(true);
                  handleAccountMenuClose();
                }}
              >
                Manage accounts
              </MenuItem>
            </>
          )}
          <MenuItem onClick={() => { handleLogout(); handleAccountMenuClose(); }}>Logout</MenuItem>
        </Menu>
      </Box>

      <Paper
        sx={{ p: 3, maxWidth: 700, mx: "auto", mt: 4 }}
        onClick={() => {
          setMenuPosition(null);
          setSelectedApp(null);
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5">Applications</Typography>
          <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
            Create New Application
          </Button>
        </Box>
        
        {/* Category Filter */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter by Category:</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              variant={categoryFilter === 'all' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleCategoryChange('all')}
            >
              All ({applications.length})
            </Button>
            <Button 
              variant={categoryFilter === 'active' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleCategoryChange('active')}
            >
              Active
            </Button>
            <Button 
              variant={categoryFilter === 'archive' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleCategoryChange('archive')}
            >
              Archived
            </Button>
            <Button 
              variant={categoryFilter === 'uncategorized' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleCategoryChange('uncategorized')}
            >
              Uncategorized
            </Button>
          </Box>
        </Box>
        <List>
          {applications.map((app) => {
            const attachmentList = Array.isArray(app.attachments)
              ? app.attachments
              : typeof app.attachments === "string" && app.attachments.length > 0
                ? app.attachments.split(",")
                : [];
            // Default to "new" if status is missing
            const status = app.status || "new";
            return (
              <ListItem
                key={app.id}
                alignItems="flex-start"
                button
                onClick={() => {
                  navigate(`/applications/${app.id}`);  // Navigate to the detail page
                }}
                sx={{
                  mb: 2,
                  border: "1px solid #eee",
                  borderRadius: 2,
                  backgroundColor: 
                    ["accepted", "approved"].includes(status) ? "#ccffcc" :
                    ["submission_review", "assessment_review"].includes(status) ? "#f9dd81ff" :
                    ["rejected", "denied"].includes(status) ? "#ffcccc" :
                    "#f5f5f5",
                  cursor: "pointer"
                }}
              >
                <ListItemText
                  primary={
                    <span>
                      <b>ID:</b> {app.id}
                      <br />
                      <b>Address:</b> {app.address}
                      <br />
                      <b>Proposed Development:</b> {app.proposedDevelopment || app.proposed_development}
                      <br />
                      <b>Status:</b> <span style={{ fontWeight: "bold" }}>{status}</span>
                      <br />
                      <b>Category:</b> <span style={{ fontWeight: "bold", color: app.category === 'active' ? '#4caf50' : app.category === 'archive' ? '#ff9800' : '#9e9e9e' }}>
                        {app.category || 'Uncategorized'}
                      </span>
                      <br />
                      <b>Attachments:</b>{" "}
                      {(() => {
                        let attachmentList = [];
                        try {
                          // Try parsing as JSON first
                          if (typeof app.attachments === 'string') {
                            attachmentList = JSON.parse(app.attachments);
                          } else if (Array.isArray(app.attachments)) {
                            attachmentList = app.attachments;
                          }
                        } catch (e) {
                          // Fallback to legacy comma-separated string handling
                          attachmentList = app.attachments ? app.attachments.split(',') : [];
                        }
                        
                        return attachmentList.length > 0 
                          ? attachmentList.map((file, idx) => (
                              <span key={idx} style={{ marginRight: 8 }}>
                                <AttachFileIcon fontSize="small" sx={{ verticalAlign: "middle" }} />
                                {file.name || file}
                              </span>
                            ))
                          : <span style={{ color: "#888" }}>None</span>
                      })()}
                    </span>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" color="error" onClick={e => { e.stopPropagation(); handleDelete(app.id); }}>
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
                <Menu
                  open={Boolean(menuPosition) && selectedApp?.id === app.id}
                  onClose={() => {
                    setMenuPosition(null);
                    setSelectedApp(null);
                  }}
                  anchorReference="anchorPosition"
                  anchorPosition={menuPosition ? { top: menuPosition.top, left: menuPosition.left } : undefined}
                >
                  <MenuItem
                    onClick={() => {
                      setAssessmentText(app.initial_assessment || "No assessment available.");
                      setAssessmentOpen(true);
                      handleMenuClose();
                    }}
                  >
                    Show Initial Document Assessment
                  </MenuItem>
                  <MenuItem onClick={handleAnalyze} disabled={analyzeLoading}>
                    {analyzeLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                    Analyze with POPPi
                  </MenuItem>
                </Menu>
              </ListItem>
            );
          })}
          {applications.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
              No applications yet.
            </Typography>
          )}
        </List>
        <Dialog open={open} onClose={() => { setOpen(false); setFormMode('quick'); }} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Application</DialogTitle>
          <form onSubmit={handleAdd}>
            <DialogContent>
              {/* Mode Selection */}
              <Box sx={{ mb: 3 }}>
                {/* Quick Mode Card */}
                <Card 
                  sx={{ 
                    mb: 2, 
                    border: formMode === 'quick' ? '2px solid #1976d2' : '1px solid #ddd',
                    cursor: 'pointer'
                  }}
                  onClick={() => setFormMode('quick')}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" color={formMode === 'quick' ? 'primary' : 'text.primary'}>
                        Quick Create
                      </Typography>
                      {formMode === 'quick' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Upload files and AI will extract details and run full compliance analysis
                    </Typography>
                  </CardContent>
                  <Collapse in={formMode === 'quick'}>
                    <CardContent sx={{ pt: 0 }}>
                      <Box
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('attachments-input').click()}
                        sx={{
                          border: `2px dashed ${isDragOver ? '#1976d2' : '#ddd'}`,
                          borderRadius: 2,
                          padding: 3,
                          textAlign: 'center',
                          backgroundColor: isDragOver ? '#f3f9ff' : '#fafafa',
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer',
                          mb: 2,
                          position: 'relative',
                          '&:hover': {
                            borderColor: '#1976d2',
                            backgroundColor: '#f3f9ff'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <AttachFileIcon 
                            sx={{ 
                              fontSize: 40, 
                              color: isDragOver ? '#1976d2' : '#999' 
                            }} 
                          />
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: isDragOver ? '#1976d2' : 'text.primary',
                              fontWeight: isDragOver ? 'bold' : 'normal'
                            }}
                          >
                            {isDragOver ? 'Drop files here' : 'Drag & drop files here or click to browse'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            * At least one file is required
                          </Typography>
                        </Box>
                        <input
                          id="attachments-input"
                          name="attachments"
                          type="file"
                          multiple
                          hidden
                          onChange={handleChange}
                          style={{ display: 'none' }}
                        />
                      </Box>
                      
                      {Array.isArray(form.attachments) && form.attachments.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Files to upload:</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {Array.from(form.attachments).map((file, idx) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'space-between',
                                  backgroundColor: '#f5f5f5',
                                  padding: 1,
                                  borderRadius: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AttachFileIcon fontSize="small" />
                                  <Typography variant="body2">{file.name}</Typography>
                                </Box>
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeFile(idx);
                                  }}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Collapse>
                </Card>

                {/* Advanced Mode Card */}
                <Card 
                  sx={{ 
                    border: formMode === 'advanced' ? '2px solid #1976d2' : '1px solid #ddd',
                    cursor: 'pointer'
                  }}
                  onClick={() => setFormMode('advanced')}
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" color={formMode === 'advanced' ? 'primary' : 'text.primary'}>
                        Advanced Create
                      </Typography>
                      {formMode === 'advanced' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Provide detailed information manually
                    </Typography>
                  </CardContent>
                  <Collapse in={formMode === 'advanced'}>
                    <CardContent sx={{ pt: 0 }}>
                      <TextField
                        label="Address"
                        name="address"
                        value={form.address}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        label="Proposed Development"
                        name="proposedDevelopment"
                        value={form.proposedDevelopment}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                      
                      {/* Drag and Drop File Upload Area */}
                      <Box
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('advanced-attachments-input').click()}
                        sx={{
                          border: `2px dashed ${isDragOver ? '#1976d2' : '#ddd'}`,
                          borderRadius: 2,
                          padding: 2,
                          textAlign: 'center',
                          backgroundColor: isDragOver ? '#f3f9ff' : '#fafafa',
                          transition: 'all 0.2s ease-in-out',
                          cursor: 'pointer',
                          mb: 2,
                          position: 'relative',
                          '&:hover': {
                            borderColor: '#1976d2',
                            backgroundColor: '#f3f9ff'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <AttachFileIcon 
                            sx={{ 
                              fontSize: 32, 
                              color: isDragOver ? '#1976d2' : '#999' 
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: isDragOver ? '#1976d2' : 'text.primary',
                              fontWeight: isDragOver ? 'bold' : 'normal'
                            }}
                          >
                            {isDragOver ? 'Drop files here' : 'Drag & drop files here or click to browse'}
                          </Typography>
                        </Box>
                        <input
                          id="advanced-attachments-input"
                          name="attachments"
                          type="file"
                          multiple
                          hidden
                          onChange={handleChange}
                          style={{ display: 'none' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <input
                          type="checkbox"
                          name="assessFiles"
                          checked={form.assessFiles}
                          onChange={handleChange}
                          id="assess-files-checkbox"
                        />
                        <label htmlFor="assess-files-checkbox" style={{ marginLeft: 8, cursor: 'pointer' }}>
                          Assess files
                        </label>
                      </Box>

                      {Array.isArray(form.attachments) && form.attachments.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>Files to upload:</Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {Array.from(form.attachments).map((file, idx) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'space-between',
                                  backgroundColor: '#f5f5f5',
                                  padding: 1,
                                  borderRadius: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AttachFileIcon fontSize="small" />
                                  <Typography variant="body2">{file.name}</Typography>
                                </Box>
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    removeFile(idx);
                                  }}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Collapse>
                </Card>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setOpen(false); setFormMode('quick'); }} disabled={creating}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={creating || (formMode === 'quick' && form.attachments.length === 0) || (formMode === 'advanced' && (!form.address || !form.proposedDevelopment))}
              >
                {creating ? <CircularProgress size={24} sx={{ mr: 1 }} /> : null}
                Create
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <Dialog open={assessmentOpen} onClose={() => setAssessmentOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Initial Document Assessment</DialogTitle>
          <DialogContent>
            <ReactMarkdown>
              {assessmentText}
            </ReactMarkdown>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssessmentOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}

export default ApplicationsList;